import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';

const JobContext = createContext();

export const useJobs = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};

export const JobProvider = ({ children }) => {
  const [jobs, setJobs] = useState(() => {
    // Initialize state from localStorage
    try {
      const storedJobs = localStorage.getItem('jobs');
      return storedJobs ? JSON.parse(storedJobs) : [];
    } catch (error) {
      console.error('Error loading jobs from localStorage:', error);
      return [];
    }
  });

  // Save jobs to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('jobs', JSON.stringify(jobs));
    } catch (error) {
      console.error('Error saving jobs to localStorage:', error);
    }
  }, [jobs]);

  const addJob = async (jobData) => {
    try {
      const newJob = {
        ...jobData,
        id: uuidv4(),
        createdAt: new Date().toISOString(),
        status: 'pending'
      };

      setJobs(prevJobs => [...prevJobs, newJob]);
      toast.success('Job created successfully!');
      return newJob;
    } catch (error) {
      console.error('Error adding job:', error);
      toast.error('Failed to create job');
      throw error;
    }
  };

  const updateJob = async (id, updatedData) => {
    try {
      setJobs(prevJobs =>
        prevJobs.map(job =>
          job.id === id ? { ...job, ...updatedData } : job
        )
      );
      toast.success('Job updated successfully!');
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error('Failed to update job');
      throw error;
    }
  };

  const deleteJob = async (id) => {
    try {
      setJobs(prevJobs => prevJobs.filter(job => job.id !== id));
      toast.success('Job deleted successfully!');
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
      throw error;
    }
  };

  const getJob = (id) => {
    return jobs.find(job => job.id === id);
  };

  const getAllJobs = () => {
    return jobs;
  };

  const refreshJobs = async () => {
    try {
      const storedJobs = localStorage.getItem('jobs');
      if (storedJobs) {
        const parsedJobs = JSON.parse(storedJobs);
        // Only update if the jobs are different
        if (JSON.stringify(parsedJobs) !== JSON.stringify(jobs)) {
          setJobs(parsedJobs);
        }
      }
    } catch (error) {
      console.error('Error refreshing jobs:', error);
    }
  };

  const value = {
    jobs,
    addJob,
    updateJob,
    deleteJob,
    getJob,
    getAllJobs,
    refreshJobs
  };

  return (
    <JobContext.Provider value={value}>
      {children}
    </JobContext.Provider>
  );
};

export default JobContext; 